import { useEffect, useRef, useState } from 'react'
import { colors } from '@fitpet/constants-global'
import { FitpetIcon } from '@fitpet/ui/components'
import { dehydrate } from '@tanstack/react-query'
import cookie from 'cookie'
import { GetServerSidePropsContext } from 'next/types'
import { useSelector } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { getGetProductDetailQueryKey, getProductDetail } from '@/apis/rest/generated/hooks'
import { Typo, TypoVariant } from '@/components/common/typography'
import { SkeletonLine } from '@/components/domains/category/SkeletonLine'
import { LayoutFooter } from '@/components/domains/layouts'
import SeoHeader from '@/components/domains/layouts/header/SeoHeader'
import { useRecentlyProductList } from '@/components/domains/mypage/favorites/hooks/use-recently-product-list'
import { ProductDetailBrand } from '@/components/domains/product-detail/ProductDetailBrand/ProductDetailBrand'
import { ProductDetailContentImageBanner } from '@/components/domains/product-detail/ProductDetailContentImageBanner'
import { ProductDetailContentNav } from '@/components/domains/product-detail/ProductDetailContentNav'
import { ProductDetailDescriptionDynamic } from '@/components/domains/product-detail/ProductDetailDescription'
import { ProductDetailExchangeRefundGuide } from '@/components/domains/product-detail/ProductDetailExchangeRefundGuide'
import { ProductDetailFixedBottom } from '@/components/domains/product-detail/ProductDetailFixedBottom'
import { ProductDetailInformationTextBanner } from '@/components/domains/product-detail/ProductDetailInformationTextBanner'
import { ProductDetailIngredientsProductList } from '@/components/domains/product-detail/ProductDetailIngredientsProductList'
import { ProductDetailInquiry } from '@/components/domains/product-detail/ProductDetailInquiry'
import { ProductDetailMainImageBanner } from '@/components/domains/product-detail/ProductDetailMainImageBanner'
import { ProductDetailMaxPriceBenefit } from '@/components/domains/product-detail/ProductDetailMaxPriceBenefit'
import { ProductDetailPriceInformation } from '@/components/domains/product-detail/ProductDetailPriceInformation'
import { ProductDetailRecentlyProductList } from '@/components/domains/product-detail/ProductDetailRecentlyProductList'
import { ProductDetailRelatedProductList } from '@/components/domains/product-detail/ProductDetailRelatedProductList'
import { ProductDetailReviews } from '@/components/domains/product-detail/ProductDetailReviews'
import { ProductDetailShareLink } from '@/components/domains/product-detail/ProductDetailShareLink'
import { ProductDetailShipping } from '@/components/domains/product-detail/ProductDetailShipping'
import { ProductDetailSimilarProductList } from '@/components/domains/product-detail/ProductDetailSimilarProductList'
import { ProductDetailTags } from '@/components/domains/product-detail/ProductDetailTags'
import { ProductDetailTopNavi } from '@/components/domains/product-detail/ProductDetailTopNavi'
import { ProductDetailChallengeTooltip } from '@/components/domains/product-detail/product-detail-challenge-tooltip'
import { HttpStatusCode } from '@/constants/http-status-code'
import { LocationEnum } from '@/constants/location.enum'
import { MembershipStatusEnum } from '@/constants/membership-status.enum'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import { useCustomRouter } from '@/containers/hooks'
import { useThrottle } from '@/containers/hooks/common/useThrottle.hook'
import { useQueryProductDetail } from '@/containers/hooks/product-detail/useQueryProductDetail'
import { UserContainer } from '@/containers/users/UserContainer'
import { queryClient } from '@/lib/queryClient'
import { RootState } from '@/stores/store'
import { Base64ConverterUtil } from '@/utils/base64-converter.util'
import { cookieKey } from '@/utils/cookie.util'
import { decodeId } from '@/utils/utilApi'
import { getImageUrl } from '@/utils/utilURL'

type ScrollRefType = {
  [key: string]: HTMLElement | null
}

const ProductDetailPage = ({
  query,
}: {
  query: {
    productId: number
    promotionId: number
  }
}) => {
  const { me, meIsLoading: isLoadingUserLoggedIn } = UserContainer.useContainer()
  const { color } = useTheme()
  const { query: browserUrlQuery } = useCustomRouter()
  const { productId, promotionId } = query
  const { data } = useQueryProductDetail({
    productId: Number(productId),
    promotionId: Number(promotionId),
  })

  const { addRecentlyProduct } = useRecentlyProductList({ length: 0 })

  const isShowTopSlimBanner = useSelector((state: RootState) => state.reduxUIReducers.isShowTopSlimBanner)
  const scrollElementsArrayRef = useRef<ScrollRefType>({})
  const isScrolling = useRef(false)
  const [scrollContentActiveTab, setScrollContentActiveTab] = useState('info')
  const scrollContentActiveTabRef = useRef(scrollContentActiveTab)
  const isUserLoggedIn = !!me
  const isMembership = me?.membershipStatus !== MembershipStatusEnum.None
  const mainBannerImages = [
    // animatedMainImage(webp)가 있으면 기존 이미지들보다 먼저 노출
    ...(data?.animatedMainImage?.path ? [data.animatedMainImage.path] : []),
    data?.mainImage?.path || '',
    ...(data?.subImages.map((subImage) => subImage.path) || []),
  ].map((image) => getImageUrl(image) || '')

  const { trackViewProductDetailEvent } = useProductDetailEventTracker()

  useEffect(() => {
    scrollContentActiveTabRef.current = 'info'
    setScrollContentActiveTab('info')
  }, [browserUrlQuery])

  const changeContentNavStyle = () => {
    if (isScrolling.current) {
      return
    }
    const productDetailTopNaviHeight = document.querySelector('.product-detail-top-navi')?.clientHeight || 0
    const productDetailContentNavHeight = document.querySelector('.product-detail-page__content-nav')?.clientHeight || 0
    Object.keys(scrollElementsArrayRef.current).forEach((key) => {
      if (
        ((scrollElementsArrayRef.current[key] && scrollElementsArrayRef.current[key]?.offsetTop) || 0) -
          (productDetailTopNaviHeight + productDetailContentNavHeight) <=
        window.scrollY
      ) {
        if (key !== scrollContentActiveTabRef.current) {
          setScrollContentActiveTab(key)
          scrollContentActiveTabRef.current = key
        }
      }
    })
  }
  const throttledChangeContentNavStyle = useThrottle(changeContentNavStyle, 300)

  useEffect(() => {
    if (!data) return
    trackViewProductDetailEvent({
      product: data,
      query: browserUrlQuery,
    })
    addRecentlyProduct({
      productId: Base64ConverterUtil.convertNumberIdToBase64ByKey({ key: 'ProductType', id: data.id }),
      image: mainBannerImages[0],
      productPromotionId: Base64ConverterUtil.convertNumberIdToBase64ByKey({
        key: 'ProductPromotionType',
        id: data.promotion?.id,
      }),
      salePrice: data.salePrice,
      salePriceAfterCoupon: data.salePriceAfterCoupon,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    window.addEventListener('scroll', throttledChangeContentNavStyle)
    return () => {
      window.removeEventListener('scroll', throttledChangeContentNavStyle)
    }
  }, [throttledChangeContentNavStyle])

  if (!data) return null

  const handleClickReview = () => {
    handleClickNavTab('review')
  }

  const handleClickNavTab = (tab: string) => {
    let scrollTimeout: NodeJS.Timeout
    isScrolling.current = true
    setScrollContentActiveTab(tab)
    scrollContentActiveTabRef.current = tab

    const productDetailTopNaviHeight = document.querySelector('.product-detail-top-navi')?.clientHeight || 0
    const productDetailContentNavHeight = document.querySelector('.product-detail-page__content-nav')?.clientHeight || 0
    const scrollOffsetTop = scrollElementsArrayRef.current[tab]?.offsetTop || 0

    if (scrollElementsArrayRef.current[tab] && productDetailTopNaviHeight && productDetailContentNavHeight) {
      window.scrollTo({
        top: scrollOffsetTop - (productDetailTopNaviHeight + productDetailContentNavHeight),
        behavior: 'smooth',
      })
    }

    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }

      scrollTimeout = setTimeout(() => {
        isScrolling.current = false
        window.removeEventListener('scroll', handleScroll)
      }, 100)
    }

    window.addEventListener('scroll', handleScroll)
  }

  const handleClickDetailFoldButton = () => {
    handleClickNavTab('info')
  }

  const SEO_DATA = {
    // 다른 페이지와 일관성을 맞추기 위해 '핏펫몰' 대신 '핏펫' 사용
    title: `${data.name} - 핏펫`,
    description: `${data.name}에 대한 제품 정보와 할인과 쿠폰 정보를 지금 바로 핏펫에서 확인해보세요.`,
    imageUrl: getImageUrl(data.mainImage?.path) || '',
    pathname: `/products/${productId}`,
    productData: {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: data.name,
      image: getImageUrl(data.mainImage?.path) || '',
      brand: {
        '@type': 'Brand',
        name: data.brand.name,
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'KRW',
        price:
          data.discountRateAfterCoupon && data.discountRateAfterCoupon > 0
            ? data.salePriceAfterCoupon ?? data.salePrice
            : data.salePrice,
        availability: 'http://schema.org/InStock',
      },
      ...(data.review.score > 0 && {
        review: {
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: data.review.score,
            bestRating: '5',
          },
          author: {
            '@type': 'Person',
            name: 'Fitpet Reviewer',
          },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: data.review.score,
          reviewCount: data.review.count,
        },
      }),
    },
  }

  return (
    <ProductDetailPageWrapper className="product-detail-page">
      <SeoHeader
        title={SEO_DATA.title}
        metaTitle={SEO_DATA.title}
        metaDescription={SEO_DATA.description}
        imageUrl={SEO_DATA.imageUrl}
        pathname={SEO_DATA.pathname}
        jsonLd={SEO_DATA.productData}
      />
      <ProductDetailTopNavi />
      <ProductDetailMainImageBanner
        key={data.id}
        images={mainBannerImages}
        sticker={data.sticker}
        name={data.name}
        endedAt={data.promotion?.endedAt ?? ''}
        isTimeSale={data.isTimeSale}
        isRealSoldOut={data.isRealSoldOut}
        isNotAvailable={data.isNotAvailable}
      />
      <section className="product-detail-page__information">
        <div className="product-detail-page__title">
          <Typo variant={TypoVariant.Heading6Regular} color={color.gray900}>
            {data.name}
          </Typo>
          <ProductDetailShareLink
            name={data.name}
            productId={data.id}
            productPromotionId={data.promotion?.id}
            location={LocationEnum.ProductDetail}
            discountRate={data.discountRate}
            reviewAvgScore={data.review.score}
            reviewAvgCount={data.review.count}
            price={data.salePrice}
          />
        </div>
        {!!data.review.count && (
          <div className="product-detail-page__review" onClick={handleClickReview}>
            <div className="product-detail-page__review-score">
              <FitpetIcon icon="icStar" size={12} color={colors.YELLOW_500} />
              <Typo variant={TypoVariant.Body5Medium} color={color.gray700}>
                {data.review.score}
              </Typo>
            </div>
            <div className="product-detail-page__review-count">
              <Typo variant={TypoVariant.Body5Regular} color={color.gray400}>
                ({data.review.count.toLocaleString('ko-KR')})
              </Typo>
              <FitpetIcon icon="icChevronRight" size={12} color={colors.GRAY_500} />
            </div>
          </div>
        )}
        <div className="product-detail-page__price-information">
          <ProductDetailPriceInformation
            isUserLoggedIn={isUserLoggedIn}
            hasApplicableProductCoupon={data.hasApplicableProductCoupon || false}
            hasUnreceivedProductCoupon={data.hasUnreceivedProductCoupon || false}
            productId={data.id}
            productName={data.name}
            promotionId={data.promotion?.id}
            discountRateAfterCoupon={data.discountRateAfterCoupon}
            price={data.price}
            salePrice={data.salePrice}
            salePriceAfterCoupon={data.salePriceAfterCoupon}
            salePriceAfterWelcomeCoupon={data.specialPromotionPrice?.salePriceAfterSignupCoupon}
            discountRate={data.discountRate || 0}
            maxOptionSkuCount={data.maxOptionSkuCount}
            pricePerSku={data.pricePerSku}
            isRealSoldOut={data.isRealSoldOut}
            expectedShippingFee={data.expectedShippingFee}
          />
        </div>
        <div className="product-detail-page__max-price-benefit">
          {!isLoadingUserLoggedIn ? (
            <ProductDetailMaxPriceBenefit
              expectedMileage={data.expectedMileage}
              confirmMileageRate={data.confirmMileageRate}
              confirmMileage={data.confirmMileage}
              imageReviewMileageRate={data.imageReviewMileageRate}
              imageReviewMileage={data.imageReviewMileage}
              membershipOrderConfirmDiffMileageRate={data.membershipOrderConfirmDiffMileageRate || 0}
              membershipOrderConfirmDiffMileage={data.membershipOrderConfirmDiffMileage || 0}
              maxDiscountBenefitCoupon={data.maxDiscountBenefitCoupon}
              isMembership={isMembership}
              isUserLoggedIn={isUserLoggedIn}
            />
          ) : (
            <SkeletonMaxPriceBenefit>
              <SkeletonLine height="2rem" width="100%" />
              <SkeletonLine height="2rem" width="100%" />
              <SkeletonLine height="2rem" width="100%" />
              <SkeletonLine height="2rem" width="100%" />
            </SkeletonMaxPriceBenefit>
          )}
        </div>
        <div className="product-detail-page__information-text-banner">
          <ProductDetailInformationTextBanner id={data.id} productName={data.name} />
        </div>
      </section>
      <section className="product-detail-page__shipping">
        <ProductDetailShipping
          shippingGuide={data.shippingGuide}
          shippingNotice={data.shippingNotice}
          productId={data.id}
          productName={data.name}
          categories={data.categories}
          price={data.price}
          salePrice={data.salePrice}
          supplyType={data.supplyType}
          bundledProductExists={!!data.bundledProductExists}
        />
      </section>
      {data.brand?.isApproved && (
        <div className="product-detail-page__brand">
          <ProductDetailBrand
            brandId={data.brand.id || 0}
            brandName={data.brand.name}
            brandLogoUrl={data.brand?.image}
            productId={data.id}
            productName={data.name}
            isRealSoldOut={data.isRealSoldOut}
            price={data.price}
            discountRate={data.discountRate || 0}
            salePrice={data.salePrice}
            reviewScore={data.review.score}
            reviewCount={data.review.count}
            isTimeSale={data.isTimeSale}
            categories={data.categories}
            supplyType={data.supplyType}
          />
        </div>
      )}
      <section className="product-detail-page__content-image-banner">
        <ProductDetailContentImageBanner id={data.id} productName={data.name} />
      </section>
      <section className="product-detail-page__related-product-list">
        {/* 다른 고객이 함께 구매한 상품 */}
        <ProductDetailRelatedProductList
          discountRate={data.discountRate || 0}
          productId={data.id}
          isRealSoldOut={data.isRealSoldOut}
          productName={data.name}
          price={data.price}
          salePrice={data.salePrice}
          reviewScore={data.review.score}
          reviewCount={data.review.count}
        />
      </section>
      <section
        className={`product-detail-page__content-nav ${
          isShowTopSlimBanner && 'product-detail-page__content-nav--active'
        }`}
      >
        <ProductDetailContentNav
          productName={data.name}
          productId={data.id}
          onClickNavTab={handleClickNavTab}
          activeTab={scrollContentActiveTab}
          reviewCount={data.review.count}
        />
      </section>
      <section
        className="product-detail-page__description"
        ref={(el) => {
          scrollElementsArrayRef.current['info'] = el
        }}
      >
        <ProductDetailDescriptionDynamic
          key={data.id}
          description={data.description}
          brandId={data.brand.id || 0}
          brandName={data.brand.name}
          productId={data.id}
          productName={data.name}
          isRealSoldOut={data.isRealSoldOut}
          price={data.price}
          discountRate={data.discountRate || 0}
          salePrice={data.salePrice}
          reviewScore={data.review.score}
          reviewCount={data.review.count}
          isTimeSale={data.isTimeSale}
          categories={data.categories}
          supplyType={data.supplyType}
          productBanners={data.productBanners}
          onClickFoldButton={handleClickDetailFoldButton}
        />
      </section>
      {data.productIngredient && data.productIngredient.length > 0 && (
        <section className="product-detail-page__ingredients-list">
          <ProductDetailIngredientsProductList
            productId={data.id}
            productIngredient={data.productIngredient}
            discountRate={data.discountRate || 0}
            isRealSoldOut={data.isRealSoldOut}
            productName={data.name}
            price={data.price}
            salePrice={data.salePrice}
            reviewScore={data.review.score}
            reviewCount={data.review.count}
          />
        </section>
      )}

      <section
        ref={(el) => {
          scrollElementsArrayRef.current['review'] = el
        }}
        className="product-detail-page__reviews"
      >
        <ProductDetailReviews
          isUserLoggedIn={isUserLoggedIn}
          reviews={data.reviews}
          reviewCount={data.review.count}
          reviewScore={data.review.score}
          brandId={data.brand.id || 0}
          brandName={data.brand.name}
          productId={data.id}
          productName={data.name}
          isRealSoldOut={data.isRealSoldOut}
          price={data.price}
          discountRate={data.discountRate || 0}
          salePrice={data.salePrice}
          isTimeSale={data.isTimeSale}
          categories={data.categories}
          supplyType={data.supplyType}
        />
      </section>
      <div
        ref={(el) => {
          scrollElementsArrayRef.current['recommend'] = el
        }}
      >
        <section className="product-detail-page__similar-list">
          <ProductDetailSimilarProductList
            discountRate={data.discountRate || 0}
            productId={data.id}
            isRealSoldOut={data.isRealSoldOut}
            productName={data.name}
            price={data.price}
            salePrice={data.salePrice}
            reviewScore={data.review.score}
            reviewCount={data.review.count}
          />
        </section>
        <section className="product-detail-page__recently-list">
          <ProductDetailRecentlyProductList
            discountRate={data.discountRate || 0}
            productId={data.id}
            isRealSoldOut={data.isRealSoldOut}
            productName={data.name}
            price={data.price}
            salePrice={data.salePrice}
            reviewScore={data.review.score}
            reviewCount={data.review.count}
          />
        </section>
      </div>
      <div className="product-detail-page__inquiry-wrapper">
        <section
          ref={(el) => {
            scrollElementsArrayRef.current['inquiry'] = el
          }}
          className="product-detail-page__inquiry"
        >
          <ProductDetailInquiry count={data.qnaCount} id={data.id} />
        </section>
        <section className="product-detail-page__tags">
          <ProductDetailTags tags={data.searchKeywords} productId={data.id} productName={data.name} />
        </section>
        <section className="product-detail-page__exchange-refund-guide">
          <ProductDetailExchangeRefundGuide productId={data.id} />
        </section>
      </div>

      {!isLoadingUserLoggedIn && <ProductDetailChallengeTooltip />}
      <section className="product-detail-page__fixed-bottom">
        {!isLoadingUserLoggedIn && (
          <ProductDetailFixedBottom productDetailData={data} isUserLoggedIn={isUserLoggedIn} />
        )}
      </section>

      <footer className="product-detail-page__footer">
        <LayoutFooter />
      </footer>
    </ProductDetailPageWrapper>
  )
}

export default ProductDetailPage

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { productId, promotion } = context.query as {
    productId: string
    promotion: string
  }
  // id가 없는 경우 404로 이동
  if (!productId) {
    return {
      notFound: true,
    }
  }

  /*
   * 원링크 URL 의 경우 pc 버전 리다이렉트 처리가 문제가 있어서 프론트에서 queryString 의 deep link 를 파싱해서 처리한다
   * ex : https://www.fitpetmall.com/mall/products/0000000000?af_force_deeplink=true&deep_link_sub1=https%3A%2F%2Fwww.fitpetmall.com%2Fmall%2Fproducts%2F1000001682%3Flocation%3Drestock&source_caller=ui&pid=Product_Share&is_retargeting=true&af_click_lookback=1h&shortlink=7dwegezf&deep_link_value=fitpet%3A%2F%2Fdetail-url&c=%EA%B3%B5%EC%9C%A0%ED%95%98%EA%B8%B0%20%ED%94%84%EB%A1%9C%EB%8D%95%EC%85%98_220429
   */
  if (productId === '0000000000' && context?.query?.deep_link_sub1) {
    return {
      redirect: {
        permanent: false,
        destination: context.query.deep_link_sub1.toString(),
      },
    }
  }

  const getDecodedPromotionId = (str: string) => {
    if (!isNaN(Number(str))) {
      return Number(str)
    }

    try {
      const decodedPromotionId = decodeId(str).id
      return decodedPromotionId === undefined ? false : Number(decodedPromotionId)
    } catch {
      return false
    }
  }

  const requestPromotionId = getDecodedPromotionId(promotion)

  if (!productId) {
    return {
      notFound: true,
    }
  }

  const fetchProductDetail = () => {
    const options = requestPromotionId ? { promotionId: requestPromotionId } : {}
    const cookieHeader = context.req.headers.cookie || ''
    const cookies = cookie.parse(cookieHeader)
    const token = cookies[cookieKey]
    return getProductDetail(Number(productId), options, {
      headers: {
        Authorization: token,
      },
    })
  }

  try {
    const queryKey = getGetProductDetailQueryKey(Number(productId), {
      ...(requestPromotionId && { promotionId: requestPromotionId }),
    })

    await queryClient.invalidateQueries(queryKey)

    const result = await queryClient.fetchQuery(queryKey, fetchProductDetail)

    if (result.isNotAvailable) {
      context.res.statusCode = HttpStatusCode.Gone
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error({ error })
    return {
      notFound: true,
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      query: {
        productId: Number(productId),
        promotionId: Number(requestPromotionId),
      },
    },
  }
}

const ProductDetailPageWrapper = styled.div`
  .product-detail-page {
    &__information {
      padding: 1.6rem;
      border-bottom: 0.1rem solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
    }
    &__title {
      display: flex;
      justify-content: space-between;
      gap: 2rem;
      align-items: flex-start;
    }
    &__review {
      margin-top: 0.8rem;
      cursor: pointer;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.2rem;
    }
    &__review-score {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.2rem;
    }
    &__review-count {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.2rem;
    }
    &__price-information {
      margin-top: 2.4rem;
    }
    &__max-price-benefit {
      margin-top: 1.6rem;
    }
    &__fitpet-plus-benefit {
      margin-top: 1.6rem;
    }
    &__information-text-banner {
      margin-top: 1.6rem;
    }
    &__shipping {
      padding: 1.6rem 1.6rem 2.4rem 1.6rem;
    }
    &__brand {
      border-top: 0.8rem solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
    }
    &__related-product-list {
      padding: 2.4rem 0;
      border-bottom: 0.8rem solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
    }
    &__content-nav {
      /* 상품 리스트 Right 버튼이 z-index: 20임 */
      /* src/components/common/buttons/ButtonSlideScrollRight.tsx */
      z-index: 21;
      background-color: ${({
        theme: {
          color: { grayWhite },
        },
      }) => grayWhite};
      border-bottom: 0.1rem solid
        ${({
          theme: {
            color: { gray50 },
          },
        }) => gray50};
      position: sticky;
      top: 5.5rem; // 5.6rem이 페이지 header높이인데 border 0.1rem이 있어서 5.5rem으로 조정
      padding: 0 1.6rem;
    }
    &__content-nav--active {
      top: calc(5.5rem + 4.2rem);
    }

    &__description {
      border-bottom: 0.8rem solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
    }
    &__reviews {
      border-bottom: 0.8rem solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
    }
    &__inquiry-wrapper {
      margin-top: 4rem;
      border-top: 0.8rem solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
    }
    &__inquiry {
      padding: 1.2rem 1.6rem;
      border-bottom: 0.8rem solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
    }

    &__tags {
      padding: 1.2rem 1.6rem 2.4rem;
      border-bottom: 0.8rem solid
        ${({
          theme: {
            color: { gray70 },
          },
        }) => gray70};
    }
    &__exchange-refund-guide {
      margin-bottom: -4rem;
      border-bottom: 0;
      padding: 1.2rem 1.6rem;
    }
    &__fixed-bottom {
      width: 100%;
      max-width: 576px;
      position: fixed;
      bottom: 0;
      z-index: 100;
    }
    &__footer {
      margin-top: 4rem;
      // fixed-bottom 과 겹치지 않게 하기 위해 padding-bottom 추가
      padding-bottom: calc(3rem + env(safe-area-inset-bottom));
    }
  }
`

const SkeletonMaxPriceBenefit = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`
